<template>
  <div class="tasks-layout__content">
    <!-- main content header -->
    <div class="tasks-layout__header">
      <div
        v-if="!searchIsOpen"
        class="typo-nav-item tasks-layout__title"
        @click="$emit('showCategories')"
      >
        {{ title }}
        <UseSvg
          class="icon icon--small hide-for-medium"
          name="arrow_down_16"
        />
      </div>
      <div
        v-else
        class="input input--flex-grow input--tasks"
      >
        <div :class="{ 'input__prefix': true, 'input__prefix--hilight': search }">
          <UseSvg
            class="icon"
            name="search"
          />
        </div>
        <input
          ref="searchInput"
          v-model="search"
          :aria-label="$t('tasks.search')"
          :placeholder="searchPlaceholderText"
        >
        <button
          v-if="search || searchIsOpen"
          class="input__clear"
          type="button"
          @click="clearSearch"
        >
          <UseSvg
            :aria-label="$t('general.close_search')"
            :class="{ 'icon': true, 'icon--small': !searchIsOpen }"
            :name="searchIsOpen ? 'close_circle_24' : 'close_circle_16'"
          />
        </button>
      </div>

      <div class="tasks-layout__header-right">
        <UseSvg
          v-if="!searchIsOpen"
          :aria-label="$t('tasks.search_tasks')"
          class="icon"
          name="search"
          @click="openSearch"
        />
        <DropdownLink
          v-if="breakpointMedium"
          :items="[{ label: $t('general.csv'), path: csvTaskDownloadPath }]"
          :modifiers="['right-border', 'left-border', 'margin-left']"
        />
        <div
          v-if="breakpointMedium"
          class="tasks-layout__filters typo-small"
        >
          <IssueFilter
            v-if="tasks?.length > 0"
            :labelOptions="taskLabelOptions"
            :modifiers="['no-padding-right']"
            :sortOptions="sortByOptions"
            @set-filter-options="setFilterOptions"
          />
        </div>
      </div>
    </div>

    <!-- main content sub header -->
    <div class="tasks-layout__sub-header hide-for-medium">
      <DropdownLink
        :items="[{ label: $t('general.csv'), path: csvTaskDownloadPath }]"
        :modifiers="['right-border', 'margin-left']"
      />
      <IssueFilter
        v-if="!loading"
        :labelOptions="taskLabelOptions"
        :modifiers="['no-padding-right']"
        :sortOptions="sortByOptions"
        @set-filter-options="setFilterOptions"
      />
    </div>

    <EmptyMessage
      v-if="tasks?.length === 0 && !searchResults"
      v-bind="emptyState"
    >
      <EmptyTaskListSvg />
    </EmptyMessage>
    <template v-if="search && searchResults">
      <ul
        v-if="searchResults.length > 0"
        class="tasks-layout__tasks-list tasks-layout__tasks-list--scrollable"
      >
        <TaskItem
          v-for="task in searchResults"
          :key="task.id"
          :class="{ 'tasks-layout__task-item--selected': task.id === isActive }"
          :task="task"
          @click="isActive = task.id"
          @showTaskDetail="showTaskDetail(task)"
        />
      </ul>
      <EmptyMessage
        v-else
        :message="$t('general.no_results')"
      >
        <UseSvg name="magnifying_glass_circle" />
      </EmptyMessage>
    </template>
    <ul
      v-if="!loading && !searchResults"
      class="tasks-layout__tasks-list tasks-layout__tasks-list--scrollable"
    >
      <TaskItem
        v-for="task in filteredTasks"
        :key="task.id"
        :class="{ 'tasks-layout__task-item--selected': task.id === isActive }"
        :task="task"
        @click="isActive = task.id"
        @showTaskDetail="showTaskDetail(task)"
      />
    </ul>
    <Loader
      v-if="loading"
      class="tasks-layout__loader"
    />
  </div>
  <!-- Category Item Detail Panel -->
  <div :class="{ 'tasks-layout__right scroll-container': true, 'show-for-medium': !selectedTask }">
    <!-- Back button for mobile  -->
    <HeaderBar
      class="hide-for-medium"
      :modifiers="['dark']"
    >
      <a
        aria-label="Back"
        class="header-bar__title typo-nav-item"
        @click="selectedTask = null"
      >
        <UseSvg
          class="icon"
          name="arrow_left"
        />
        <div class="typo-small-title">{{ $t('nav.tasks') }}</div>
      </a>
    </HeaderBar>

    <!-- Task detail -->
    <div
      v-if="selectedTask"
      class="tasks-layout__task-detail"
    >
      <p class="tasks-layout__task-associated-issue typo-ignore typo-small-title">
        {{ $t('tasks.associated_issue') }}
      </p>

      <ul class="tasks-layout__task-issue">
        <IssueCard
          :issue="selectedTask.issue"
          overrideIssuePath="shaftV2Issue"
          :responsive="false"
          :showDescription="false"
        />
      </ul>
      <div class="tasks-layout__task-content">
        <div class="tasks-layout__task-bar">
          <span class="tasks-layout__task-type typo-tiny">
            {{ $t(`tasks.type_names.${selectedTask.task_type}`) }}
          </span>
          <div
            v-if="selectedTask.policy.edit"
            class="dropdown"
          >
            <Popper
              v-model:open="menuOpen"
              placement="bottom-end"
            >
              <template #trigger>
                <div class="dropdown__trigger">
                  <UseSvg
                    class="icon"
                    name="vertical_ellipsis"
                  />
                </div>
              </template>
              <div class="menu menu--dropdown">
                <ul class="menu__items">
                  <li class="menu-item typo-list-item menu-item--dropdown">
                    <button
                      class="menu-item__link"
                      type="button"
                      @click="editTask"
                    >
                      {{ $t('tasks.edit_task') }}
                    </button>
                  </li>
                  <li
                    :class="{
                      'menu-item typo-list-item menu-item--dropdown': true,
                      'menu-item--disabled': taskIsComplete,
                    }"
                  >
                    <button
                      class="menu-item__link"
                      type="button"
                      @click="markTaskAs(selectedTask, 'not_applicable')"
                    >
                      {{ $t('tasks.mark_as_not_applicable') }}
                    </button>
                  </li>
                  <li
                    :class="{
                      'menu-item typo-list-item menu-item--dropdown menu-item--destructive': true,
                      'menu-item--disabled': taskIsComplete,
                    }"
                  >
                    <button
                      class="menu-item__link"
                      type="button"
                      @click="deleteTaskDialog(selectedTask.id)"
                    >
                      <p>{{ $t('tasks.delete_task') }}</p>
                    </button>
                  </li>
                </ul>
              </div>
            </Popper>
          </div>
        </div>

        <p class="tasks-layout__task-description typo-ignore typo-title">
          {{ selectedTask.description }}
        </p>
        <p
          v-if="selectedTask.due_date"
          class="tasks-layout__task-due-date typo-ignore typo-small-title"
        >
          {{ $t('tasks.task_due') }}
          {{ formatDate(selectedTask.due_date) }}
        </p>
        <ul class="tasks-layout__task-labels typo-small-title">
          <li
            v-for="(label, index) in selectedTask.labels"
            :key="index"
            class="tasks-layout__task-label"
          >
            {{ label }}
          </li>
        </ul>
        <p class="tasks-layout__task-reference typo-ignore typo-small-title">
          {{ selectedTask.external_reference }}
        </p>
        <p
          v-if="taskIsComplete"
          class="tasks-layout__task-completed-by typo-ignore typo-small-title"
        >
          {{ $t('tasks.completed_by') }}
          {{ `${selectedTask.closed_by.first_name} ${selectedTask.closed_by.last_name}` }}
        </p>
        <p
          v-if="taskIsComplete"
          class="tasks-layout__task-completed-at typo-ignore typo-small-title"
        >
          {{ formatDate(selectedTask.closed_at) }}
        </p>
      </div>
      <div
        v-if="!taskIsComplete && selectedTask.policy.edit"
        class="tasks-layout__task-actions"
      >
        <button
          class="button button--primary"
          type="button"
          @click="markTaskAs(selectedTask, 'complete')"
        >
          <UseSvg
            class="icon icon--small button__icon"
            name="checkmark"
          />
          <span class="button__label">
            {{ $t('tasks.mark_as_complete') }}
          </span>
        </button>
      </div>
      <IssueTaskFormModal
        v-if="editModalOpen"
        :errorSaving="errorSavingTask"
        :initialTask="selectedTask"
        :inspectionDate="selectedTask.issue.inspection.date"
        :issue="selectedTask.issue"
        @cancel="editModalOpen = false"
        @delete="deleteAttachment(selectedTask, $event)"
        @submit="updateTask"
      />
    </div>
    <EmptyMessage
      v-else
      :modifiers="['dark']"
      :title="$t('empty_states.no_selection')"
    />
  </div>
  <Dialog
    v-if="dialogIsOpen"
    :message="I18n.t('issues.confirm_delete_message')"
    :title="I18n.t('issues.confirm_delete_title', { type: I18n.t(`issues.type_task`) })"
    @dismissDialog="dismissDialogAction"
  >
    <template #actions>
      <button
        aria-label="delete"
        class="button button--inverse"
        type="button"
        @click="deleteTask"
      >
        {{ $t('general.yes') }}
      </button>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { formatDate } from '@/functions/date_format_helpers';
import I18n from 'i18n-js';
import Searcher from '@/functions/searcher';
import useAjax from '@/functions/use_ajax';
import { byCreatedAt } from '@/functions/order_by';

// Components
import EmptyTaskListSvg from '@/images/empty-task-list.svg';
import EmptyMessage from '@/components/generic/empty_message.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import Loader from '@/components/generic/loader.vue';
import IssueCard from '@/components/issues/issue_card.vue';
import IssueTaskFormModal from '@/components/issues/issue_task_form_modal.vue';
import TaskItem from '@/components/tasks/task_item.vue';
import Popper from '@/components/generic/popper.vue';
import Dialog from '@/components/generic/dialog.vue';
import useUploader from '@/vendor/use_uploader';
import IssueFilter from '@/components/issues/issue_filter.vue';
import DropdownLink from '@/components/generic/download_link.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
  category: { type: String, required: true },
});
const emit = defineEmits(['showCategories', 'updateCategoryCount']);

const ajax = useAjax();

const loading = ref(true);
const tasks = ref(null);
const existingLabels = ref([]);
const breakpointMedium = ref(null);
const editModalOpen = ref(false);
const isActive = ref(null);
const menuOpen = ref(false);
const route = useRoute();
const router = useRouter();
const selectedTask = ref(null);
const filters = ref({
  types: [],
  labels: [],
  conditions: [],
  priorities: [],
});
const search = ref('');
const searchInput = ref(null);
const searchResults = ref(null);
const searchIsOpen = ref(false);

const dialogIsOpen = ref(false);
const deletionId = ref(null);
const errorSavingTask = ref(null);
const uploader = useUploader();

let searcher;

/**
 * Checks task status
 */
const taskIsComplete = computed(() => {
  return selectedTask.value?.status === 'complete';
});

/**
 * Renders category title
 */
const title = computed(() => I18n.t(`tasks.categories.${props.category}`));

const searchPlaceholderText = computed(() => {
  return I18n.t('tasks.search_category', { category: I18n.t(`tasks.categories.${props.category}`).toLowerCase() });
});

const emptyState = computed(() => {
  return {
    title: I18n.t(`empty_states.tasks.${props.category}.title`),
    message: I18n.t(`empty_states.tasks.${props.category}.message`),
  };
});

/**
 * Filter (label) options
 */
const taskLabelOptions = computed(() => existingLabels.value.map((type) => ({
  value: type,
  label: type,
})));

/**
 * Filtered tasks
 */
const filteredTasks = computed(() => {
  const shouldFilter = ['types', 'labels', 'conditions', 'priorities', 'sortOption'].some((filter) => filters.value[filter]?.length > 0);
  if (!shouldFilter) return tasks.value;

  let filteredTasks = [...tasks.value];

  if (filters.value['types'].length > 0) filteredTasks = filterByType(filteredTasks);
  if (filters.value['labels'].length > 0) filteredTasks = filterByLabel(filteredTasks);
  if (filters.value['conditions'].length > 0) filteredTasks = filterByCondition(filteredTasks);
  if (filters.value['priorities'].length > 0) filteredTasks = filterByPriority(filteredTasks);
  if (filters.value['sortOption']) filteredTasks.sort(sortByCreatedAt(filters.value['sortOption']));

  return filteredTasks;
});

// sort by options
const sortByOptions = [
  { label: I18n.t('general.oldest'), value: 'oldest'},
  { label: I18n.t('general.newest'), value: 'newest'},
];

onMounted(async () => {
  loadLabels();
  await loadTasks();
  setActiveTask();
  taskIsComplete;
  trackWindowResize();
  window.addEventListener('resize', trackWindowResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', trackWindowResize);
});

watch(() => props.category, loadTasks);

watch(() => route.query.id, setActiveTask);

/**
 * Watch and set search
 */
watch(search, () => {
  if (search.value) {
    searchResults.value = searcher.search(search.value);
  } else {
    searchResults.value = null;
  }
});

/**
 * Fetch & load tasks
 */
async function loadTasks() {
  loading.value = true;
  clearSearch();

  let { ok, data } = await ajax(
    `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/tasks`,
    { query: { category: props.category } }
  );

  if (ok) {
    tasks.value = data;
    setActiveTask();
    emit('updateCategoryCount');
  }

  loading.value = false;
  searcher = new Searcher(tasks.value, { keys: ['description'] }, { threshold: 0.0 });
}

async function openSearch() {
  search.value = '';
  searchIsOpen.value = true;

  await nextTick();
  searchInput.value.focus();
}

function clearSearch() {
  search.value = '';
  searchIsOpen.value = false;
}

async function loadLabels() {
  let { ok, data } = await ajax(`/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/task_labels`);

  if (ok) {
    existingLabels.value = data;
  }
}

function trackWindowResize() {
  if (window.innerWidth > 768) {
    breakpointMedium.value = true;
  } else {
    breakpointMedium.value = false;
  }
}

/**
 * Set active task based off route params
 */
function setActiveTask() {
  if (route.query.id) {
    selectedTask.value = tasks.value.find((task) => task.id === route.query.id);
    isActive.value = selectedTask.value.id;
  } else {
    selectedTask.value = null;
    isActive.value = null;
  }
}

/**
 * Display the task details
 */
function showTaskDetail(task) {
  selectedTask.value = task;
  router.push({ query: { id: task.id } });
}

/**
 * Close edit task modal and dropdown menu
 */
function editTask() {
  editModalOpen.value = true;
  menuOpen.value = false;
}

/**
 * Filter by type only
 */
function filterByType(tasks) {
  return tasks.filter((task) =>
    filters.value['types'].includes(task.task_type)
  );
}

/**
 * Filter by label only
 */
function filterByLabel(tasks) {
  return tasks.filter((task) => {
    return task.labels.some((label) => {
      return filters.value['labels'].includes(label);
    });
  });
}

/**
 * Filter by condition only
 */
function filterByCondition(tasks) {
  return tasks.filter((task) => {
    return filters.value['conditions'].includes(task?.issue?.overall_condition_rating);
  });
}

/**
 * Filter by priority only
 */
function filterByPriority(tasks) {
  return tasks.filter((task) => {
    return filters.value['priorities'].includes(task?.issue?.priority);
  });
}

// sort by new or old
function sortByCreatedAt(sortBy){
  switch (sortBy) {
    case 'newest': return byCreatedAt.desc;
    case 'oldest': return byCreatedAt.asc;
  }
}

/**
 * Update task
 */
async function updateTask(task) {
  const tasksPath =
    `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/tasks/${task.id}`;

  let { ok } = await ajax(tasksPath, {
    method: 'PATCH',
    body: {
      task: {
        description: task.description,
        due_date: task.due_date,
        external_reference: task.external_reference,
        task_type: task.task_type,
        labels: task.labels,
        task_documents_attributes: await Promise.all(
          task.task_documents.map(formatDocumentForPost)
        ),
      },
    },
  });

  if (ok) {
    await loadTasks();
    emit('updateCategoryCount');
  } else {
    errorSavingTask.value = false;
  }

  editModalOpen.value = false;
}

const csvTaskDownloadPath = computed(() => {
  let endpoint = `/internal/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/tasks/download`;

  endpoint += `?category=${props.category}`;

  if (search.value) {
    endpoint += `&search=${search.value}`;
  }
  endpoint += filters.value['conditions'].map((item) => `&conditions[]=${item}`).join('');
  endpoint += filters.value['priorities'].map((item) => `&priorities[]=${item.replace('priority_', '')}`).join('');
  endpoint += filters.value['types'].map((item) => `&types[]=${item}`).join('');
  endpoint += filters.value['labels'].map((item) => `&labels[]=${item}`).join('');

  return endpoint;
});

async function deleteAttachment(task, updatedTask) {
  // check if any docs removed
  const deletedDocuments = task.task_documents?.filter((id) => {
    return !updatedTask.task_documents.includes(id);
  });

  // remove docs
  if (deletedDocuments) {
    deletedDocuments.forEach((document) => {
      updateAttachment(updatedTask, { id: task.id, task_documents_attributes: [{ id: document.id, _destroy: true }] });
    });
  }
}

async function formatDocumentForPost(file) {
  if (!file?.isNew) return;
  await uploadFile(file, 'attachment');
  return { file: file.file_blob.signed_id };
}

async function uploadFile(file) {
  let blob = await uploader.upload(file.upload);
  Object.assign(file.file_blob, blob);
  return blob;
}

async function updateAttachment(task, attributes) {
  /**
   * When I do the generic component I will move the link
   */
  const tasksPath =
    `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/tasks/${task.id}`;

  errorSavingTask.value = null;
  const updatedBody = { task: attributes };

  let { ok } = await ajax(tasksPath, {
    method: 'PATCH',
    body: updatedBody,
  });

  if (ok) {
    selectedTask.value.task_documents = task.task_documents;
  } else {
    errorSavingTask.value = true;
  }
}

/**
 * Mark task as:
 * newStatus eg. 'not_applicable,' 'complete'
 */
async function markTaskAs(task, newStatus) {
  const tasksPath =
    `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/tasks/${task.id}`;

  let { ok } = await ajax(tasksPath, {
    method: 'PATCH',
    body: {
      task: {
        status: newStatus,
      },
    },
  });

  if (ok) {
    emit('updateCategoryCount');
  }

  task.status = newStatus;
  tasks.value = tasks.value.filter((task) => task.status !== newStatus);
  menuOpen.value = false;
  selectedTask.value = null;
  router.replace({ query: null });
}

/**
 * Toggles dialog,
 * stores the id used for deletion
 */
function deleteTaskDialog(id) {
  dialogIsOpen.value = true;
  deletionId.value = id;
}

/**
 * Closes and clears any stored values
 */
function dismissDialogAction() {
  dialogIsOpen.value = false;
  menuOpen.value = false;
  deletionId.value = null;
}

/**
 * Delete task
 */
async function deleteTask() {
  const tasksPath =
    `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/tasks/${deletionId.value}`;

  let { ok } = await ajax(tasksPath, {
    method: 'DELETE',
  });

  if (ok) {
    emit('updateCategoryCount');
  }

  tasks.value = tasks.value.filter((task) => task.id !== deletionId.value);
  selectedTask.value = null;
  dismissDialogAction();
  router.replace({ query: null });
}

function setFilterOptions(options) {
  filters.value = { ...options };
}
</script>
